import { useEffect, useRef, useState } from "react";
import crest from "../assets/img/impact-crest.svg";

const cards = [
  {
    name: "60%+",
    role: "Faster information finding",
    source: "GoSearch AI · 0-to-1",
    scope: "Sole PM · retrieval, eval, agents, and the freemium launch",
    stats: [
      { value: "30%", label: "response accuracy lift" },
      { value: "$1M+", label: "incremental freemium revenue" },
    ],
    tags: ["RAG", "Evals", "Agents"],
    quote: "I held the launch until an answer could cite the source.",
    work: "gosearch",
  },
  {
    name: "4.0",
    role: "The app people carry, replaced",
    source: "GoSearch · iPhone, iPad, Android",
    scope: "Shipped 14 Oct 2026 · iPhone, iPad, Android, no engineering team.",
    stats: [
      { value: "3", label: "clients in one revamp" },
      { value: "0", label: "engineering hires" },
    ],
    tags: ["iOS", "iPad", "Android"],
    quote: "I replaced the app people carry. There was no team to wait for.",
    work: "gosearch-apps",
  },
  {
    name: "100+",
    role: "Apps on the integrations page",
    source: "GoSearch · indexed, federated, MCP",
    scope: "Led the catalog, then put GoSearch MCP inside Claude.",
    stats: [
      { value: "33", label: "with an MCP connector" },
      { value: "25", label: "federated, data not copied" },
    ],
    tags: ["Indexed", "Federated", "MCP"],
    quote:
      "I grew the catalog, then took GoSearch itself into Claude.",
    work: "gosearch-mcp",
  },
  {
    name: "$1M+",
    role: "Operating cost reduced",
    source: "Firstup · Predictive analytics",
    scope: "0-to-1 · two predictive apps shipped with no dedicated engineering team",
    stats: [
      { value: "90%", label: "less manual labor" },
      { value: "15+", label: "Fortune 100s, five countries" },
    ],
    tags: ["0-to-1", "Forecasting", "P&L impact"],
    quote:
      "I shipped both apps. The cost came off the operating line.",
    work: "analytics",
  },
  {
    name: "250+",
    role: "Enterprise clients onboarded",
    source: "Firstup · Data platform",
    scope: "Owned APIs, pipelines, and dashboards end to end, 2022–2025.",
    stats: [
      { value: "70%", label: "faster integrations, U.S. + E.U." },
      { value: "98%", label: "strategic roadmap hit rate" },
    ],
    tags: ["Fortune 50", "Compliance", "Platform"],
    quote:
      "The tenth customer got the same path as the first.",
    work: "data-platform",
  },
  {
    name: "20%",
    role: "Online sales lift",
    source: "Apple Online Store",
    scope: "Storefront features and campaign monitoring at commercial scale",
    stats: [
      { value: "15%", label: "engagement lift" },
      { value: "70%", label: "less engineering support time" },
    ],
    tags: ["Storefront", "100k+ campaigns", "Monitoring"],
    quote: "I shipped the storefront, and the layer behind 100k campaigns.",
    work: "apple",
  },
];

const CYCLE_MS = 4800;

const slotOf = (i, index, total) => {
  let delta = (i - index + total) % total;
  if (delta > total / 2) delta -= total;
  return delta;
};

const openCase = (id) => {
  window.history.pushState(null, "", `#work/${id}`);
  window.dispatchEvent(new HashChangeEvent("hashchange"));
};

export const Testimonials = () => {
  const total = cards.length;
  const [index, setIndex] = useState(0);
  const [stepping, setStepping] = useState(false);
  const rootRef = useRef(null);
  const pauseUntil = useRef(0);
  const hovering = useRef(false);
  const gesture = useRef(null);
  const swiped = useRef(false);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return undefined;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    setStepping(true);
    const timer = setTimeout(() => setStepping(false), 700);
    return () => clearTimeout(timer);
  }, [index]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const timer = setInterval(() => {
      if (document.hidden || hovering.current || Date.now() < pauseUntil.current) return;
      const section = rootRef.current;
      if (section) {
        const rect = section.getBoundingClientRect();
        if (rect.bottom < 80 || rect.top > window.innerHeight - 80) return;
      }
      setIndex((current) => (current + 1) % total);
    }, CYCLE_MS);
    return () => clearInterval(timer);
  }, [total]);

  const jump = (to) => {
    pauseUntil.current = Date.now() + 8000;
    setIndex(((to % total) + total) % total);
  };

  const onPointerDown = (event) => {
    if (event.pointerType === "mouse") return;
    gesture.current = { x: event.clientX, y: event.clientY, id: event.pointerId, lock: false };
  };

  const onPointerMove = (event) => {
    const current = gesture.current;
    if (!current || current.id !== event.pointerId || current.lock) return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
    if (Math.abs(dy) >= Math.abs(dx)) {
      gesture.current = null;
      return;
    }
    current.lock = true;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerUp = (event) => {
    const current = gesture.current;
    gesture.current = null;
    if (!current?.lock) return;
    const dx = event.clientX - current.x;
    if (Math.abs(dx) < 48) return;
    swiped.current = true;
    jump(index + (dx < 0 ? 1 : -1));
  };

  const onClickCapture = (event) => {
    if (!swiped.current) return;
    event.preventDefault();
    event.stopPropagation();
    swiped.current = false;
  };

  const marks = [0, 1, -1].map((offset) => cards[(index + offset + total) % total].name);

  return (
    <>
      <section className="testimonials" id="impact" ref={rootRef}>
        <div className="stack-lid" aria-hidden="true" />
        <div className="t-bg" aria-hidden="true">
          <div className="t-grid" />
          <span className="t-watermark t-watermark-a" key={`a-${index}`}>{marks[0]}</span>
          <span className="t-watermark t-watermark-b" key={`b-${index}`}>{marks[1]}</span>
          <span className="t-watermark t-watermark-c" key={`c-${index}`}>{marks[2]}</span>
        </div>
        <div className="t-wavy" style={{ backgroundImage: `url(${crest})` }} aria-hidden="true" />
        <div className="t-inner">
          <p className="t-eyebrow reveal">Impact</p>
          <h2 className="t-heading reveal">Proof in the numbers</h2>
          <div
            className="t-cards"
            id="t-cards"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={() => {
              gesture.current = null;
            }}
            onClickCapture={onClickCapture}
            onMouseEnter={() => {
              hovering.current = true;
            }}
            onMouseLeave={() => {
              hovering.current = false;
              pauseUntil.current = Date.now() + 1200;
            }}
          >
            {cards.map((card, i) => {
              const slot = slotOf(i, index, total);
              const abs = Math.abs(slot);
              return (
              <figure
                className={`t-card${abs === 0 ? " is-front" : ""}${abs === 1 ? " is-side" : ""}${abs > 1 ? " is-far" : ""}${stepping ? " is-step" : ""}`}
                style={{ "--pos": slot, "--abs": abs, zIndex: 6 - abs }}
                key={card.work}
                aria-hidden={abs > 1 ? true : undefined}
              >
                <p className="t-metric">{card.name}</p>
                <p className="t-role">{card.role}</p>
                <p className="t-source">{card.source}</p>
                <p className="t-scope">{card.scope}</p>
                <div className="t-stats">
                  {card.stats.map((stat) => (
                    <div className="t-stat" key={stat.label}>
                      <strong>{stat.value}</strong>
                      <span>{stat.label}</span>
                    </div>
                  ))}
                </div>
                <ul className="t-tags">
                  {card.tags.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
                <hr className="t-divider" />
                <blockquote className="t-quote">{card.quote}</blockquote>
                <button className="t-more" type="button" tabIndex={abs === 0 ? 0 : -1} onClick={() => openCase(card.work)}>
                  Read the case study
                  <span aria-hidden="true">→</span>
                </button>
              </figure>
              );
            })}
          </div>
          <div className="t-dots" role="tablist" aria-label="Impact stories">
            {cards.map((card, i) => (
              <button
                key={card.work}
                type="button"
                role="tab"
                className={i === index ? "t-dot is-on" : "t-dot"}
                aria-selected={i === index}
                aria-label={`${card.name}, ${card.role}`}
                onClick={() => jump(i)}
              />
            ))}
          </div>
        </div>
      </section>
      <div className="t-scrub" aria-hidden="true" />
    </>
  );
};
